import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { ItemCard, PressableFeedback } from '@heroui-pro/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ControlAction } from '../../controlCenterMeta';
import type { ActionRefusals } from '../../useControlCenterPage';
import { ActionRefusalPopover } from '../actionRefusal';

type CatalogRowProps = {
    action: ControlAction;
    isPinned: boolean;
    refusals: ActionRefusals;
    onRun: (action: ControlAction) => void;
    onTogglePin: (id: string) => void;
};

/**
 * One available action in the catalog: a transparent `ItemCard`, plus a pin.
 *
 * **No badge.** Availability is the segmented filter above this list, not
 * thirty-six chips down its right edge; a row at rest is a glyph and a name.
 * The one glyph a row can carry is the arrow, and it is explained once in the
 * card header rather than per row. `transparent` is the variant that says so —
 * the catalog is a list, and twenty-eight filled cards would read as
 * twenty-eight things competing for a press.
 *
 * The pin is a **sibling** of the card, not inside it. The card is already a
 * button, and a button inside a button is invalid markup — so the row is a flex
 * pair and only the left half is the action. The pin appears on hover and on
 * keyboard focus, except on a row that is already pinned, which keeps a filled
 * accent pin at rest. That is what makes this list double as the pin manager.
 */
export const CatalogRow: React.FC<CatalogRowProps> = ({
    action,
    isPinned,
    refusals,
    onRun,
    onTogglePin,
}) => {
    const { t } = useTranslation('pages');
    const label = t(`controlCenter.actions.${action.id}`);
    const refusal = refusals.refusalFor(action.id);

    const card = (
        <ItemCard.Content className="min-w-0">
            <ItemCard.Title>{label}</ItemCard.Title>
        </ItemCard.Content>
    );

    const glyph = action.opensOwnPage ? (
        <ItemCard.Action>
            <Icon
                aria-hidden
                className="size-4 text-muted"
                icon="solar:arrow-right-up-linear"
            />
        </ItemCard.Action>
    ) : null;

    const icon = (
        <ItemCard.Icon>
            <Icon aria-hidden className="size-4" icon={action.icon} />
        </ItemCard.Icon>
    );

    return (
        <li
            className="group flex items-center gap-1"
            {...getNodeTestIdProp(`control-center-catalog-row-${action.id}`)}
        >
            {refusal ? (
                <ActionRefusalPopover
                    action={action}
                    className="min-w-0 flex-1"
                    refusal={refusal}
                    trigger="wrap"
                >
                    {/* A plain card, not a button: the popover's trigger is the
                        button, and nesting one inside another is invalid. */}
                    <ItemCard
                        className="w-full cursor-pointer"
                        variant="transparent"
                        {...getNodeTestIdProp(
                            `control-center-catalog-run-${action.id}`,
                        )}
                    >
                        {icon}
                        {card}
                        {glyph}
                    </ItemCard>
                </ActionRefusalPopover>
            ) : (
                <ItemCard<'button'>
                    className="relative min-w-0 flex-1 cursor-pointer overflow-hidden"
                    onClick={() => onRun(action)}
                    render={(props) => <button type="button" {...props} />}
                    variant="transparent"
                    {...getNodeTestIdProp(
                        `control-center-catalog-run-${action.id}`,
                    )}
                >
                    <PressableFeedback.Highlight />
                    {icon}
                    {card}
                    {glyph}
                </ItemCard>
            )}
            <Button
                aria-label={
                    isPinned
                        ? t('controlCenter.pinned.unpin', { action: label })
                        : t('controlCenter.pinned.pin', { action: label })
                }
                className={cn(
                    'transition-opacity',
                    !isPinned &&
                        'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
                )}
                isIconOnly
                onPress={() => onTogglePin(action.id)}
                size="sm"
                variant="ghost"
                {...getNodeTestIdProp(
                    `control-center-catalog-pin-${action.id}`,
                )}
            >
                <Icon
                    aria-hidden
                    className={isPinned ? 'text-accent' : undefined}
                    icon={isPinned ? 'solar:pin-bold' : 'solar:pin-linear'}
                />
            </Button>
        </li>
    );
};
